import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import {
	QueryKeys,
	useDealQuery,
	useExcludedLendersQuery,
	useNavigateToNextPage,
	useReducedFeeLendersQuery,
} from '../../shared/hooks';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import { Deal, DealExcludedLender, DealReducedFeeLender } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import {
	CerebroBreadcrumb,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { Field, Formik } from 'formik';
import { Skeleton } from '@mui/material';
import { ExcludedLenderDisplay } from '../Lenders/ExcludedLenderDisplay';
import { DeleteExcludedLenderDialog } from '../Lenders/DeleteExcludedLenderDialog';
import { ExcludedLenderDrawer } from '../Lenders/ExcludedLenderDrawer';
import { MaxLenderCount } from '../../shared/constants';
import { getApiResponseData } from '../../shared/utils/axios';

export const CompanyExcludedLendersPage: React.FC = () => {
	const { guid } = useParams<'guid'>();

	const api = useApi();
	const snackbar = useSnackbar();

	const navigateToNextPage = useNavigateToNextPage();

	const client = useQueryClient();
	const dealQuery = useDealQuery(guid);
	const excludedLendersQuery = useExcludedLendersQuery(guid);
	const reducedFeeLendersQuery = useReducedFeeLendersQuery(guid);

	const [excludedLenderDrawerOpen, setExcludedLenderDrawerOpen] = React.useState(false);
	const [excludedLenderToDelete, setExcludedLenderToDelete] = React.useState<DealExcludedLender | null>(null);

	const onDrawersClose = () => {
		setExcludedLenderToDelete(null);
		setExcludedLenderDrawerOpen(false);
	};

	const dealMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const deal: Partial<Deal> = {
				hasExcludedLenders: values.hasExcludedLenders ?? false,
				hasReducedFeeLenders: values.hasReducedFeeLenders ?? false,
				lenderNotes: values.lenderNotes,
				lenderClarifications: values.lenderClarifications,
			};

			const { data, error } = await getApiResponseData<Deal>(
				api.patch(CerebroApiEndpoints.Deals.Root(guid), deal)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: deal => {
				client.setQueryData(QueryKeys.Deal.Root(guid), deal);
				snackbar.enqueueSnackbar(`Progress Saved.`, { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<>
			<CerebroPageContainer>
				<CerebroBreadcrumb>Exclude Lenders</CerebroBreadcrumb>

				{dealQuery.isSuccess && dealQuery.data.hasLaunched ? (
					<>
						{excludedLendersQuery.data && excludedLendersQuery.data.length > 0 ? (
							<>
								<CerebroText sx={{ marginBottom: 6 }}>
									The following lenders have been excluded from the Cerebro search and matching
									process for this financing:
								</CerebroText>

								<ExcludedLenderDisplay
									lenders={excludedLendersQuery.data}
									invalid={false}
									onDelete={setExcludedLenderToDelete}
									onAdd={setExcludedLenderDrawerOpen}
									isEmployeeUser={false}
									hasDealLaunched={dealQuery.data.hasLaunched}
									excludedLenderCount={dealQuery.data.excludedLenderCount}
								/>

								{dealQuery.data.lenderClarifications && dealQuery.data.lenderClarifications.length > 0 && (
									<>
										<CerebroLabel sx={{ marginTop: 3 }}>
											Additional information preferences
										</CerebroLabel>
										<CerebroText>{dealQuery.data.lenderClarifications}</CerebroText>
									</>
								)}
							</>
						) : (
							<>
								<CerebroText sx={{ marginBottom: 6 }}>
									No lenders have been excluded from the search and matching process for this
									financing.
								</CerebroText>

								{dealQuery.data.lenderClarifications && dealQuery.data.lenderClarifications.length > 0 && (
									<>
										<CerebroLabel sx={{ marginTop: 3 }}>
											Additional information preferences
										</CerebroLabel>
										<CerebroText>{dealQuery.data.lenderClarifications}</CerebroText>
									</>
								)}
							</>
						)}
					</>
				) : (
					<>
						<CerebroHeading size="h1" component="h1" sx={{ marginBottom: 3 }}>
							Now, please tell us about any lenders you are currently engaged with...
						</CerebroHeading>

						<CerebroText sx={{ marginBottom: 6 }}>
							Use this section to identify lenders you are currently engaged in discussions with regarding
							this financing. Lenders that you have received feedback from and are currently interested
							should be listed in the &quot;Excluded Lenders&quot; section and they will be excluded from
							the Cerebro search and matching process.
						</CerebroText>

						<Formik
							enableReinitialize
							validationSchema={ExcludedLendersSchema}
							initialValues={{
								hasExcludedLenders: dealQuery.data?.hasExcludedLenders ?? null,
								hasReducedFeeLenders: dealQuery.data?.hasReducedFeeLenders ?? null,
								lenderClarifications: dealQuery.data?.lenderClarifications ?? '',
								lenderNotes: dealQuery.data?.lenderNotes ?? '',
								excludedLenders: excludedLendersQuery.data ?? [],
								reducedFeeLenders: reducedFeeLendersQuery.data ?? [],
							}}
							onSubmit={FormikUtils.MutateOnSubmit(dealMutation.mutate)}
						>
							{({ isSubmitting, values, touched, errors, handleSubmit }) => (
								<CerebroForm onSubmit={handleSubmit}>
									<Field name="excludedLenders" type="hidden" />
									<Field name="reducedFeeLenders" type="hidden" />

									<CerebroLabel id="hasExcludedLenders" sx={{ marginTop: 3 }}>
										Do you have any lenders to exclude from this loan?
									</CerebroLabel>
									{dealQuery.isSuccess ? (
										<>
											<Field
												id="hasExcludedLenders"
												as={CerebroYesNo}
												name="hasExcludedLenders"
												disabled={isSubmitting}
												error={FormikUtils.IsInvalid('hasExcludedLenders', errors, touched)}
											/>
											{FormikUtils.IsInvalid('hasExcludedLenders', errors, touched) && (
												<CerebroText variant="error">{errors.hasExcludedLenders}</CerebroText>
											)}
										</>
									) : (
										<Skeleton height={60} />
									)}

									{values.hasExcludedLenders && (
										<>
											{excludedLendersQuery.isLoading && <Skeleton height={200} />}
											{excludedLendersQuery.isSuccess && (
												<ExcludedLenderDisplay
													lenders={excludedLendersQuery.data}
													invalid={FormikUtils.IsInvalid('excludedLenders', errors, touched)}
													onDelete={setExcludedLenderToDelete}
													onAdd={setExcludedLenderDrawerOpen}
													isEmployeeUser={false}
													hasDealLaunched={dealQuery.data?.hasLaunched ?? false}
													excludedLenderCount={
														dealQuery.data?.excludedLenderCount ?? MaxLenderCount
													}
												/>
											)}
										</>
									)}

									<CerebroLabel id="lenderClarifications" sx={{ marginTop: 3 }}>
										Additional information preferences
									</CerebroLabel>
									{dealQuery.isSuccess ? (
										<>
											<Field
												rows={4}
												as={CerebroTextarea}
												disabled={isSubmitting}
												id="lenderClarifications"
												name="lenderClarifications"
												placeholder="Add any additional information about lenders you would like to exclude or include in your deal"
												error={FormikUtils.IsInvalid('lenderClarifications', errors, touched)}
											/>
											{FormikUtils.IsInvalid('lenderClarifications', errors, touched) && (
												<CerebroText variant="error">{errors.lenderClarifications}</CerebroText>
											)}
										</>
									) : (
										<Skeleton height={60} />
									)}

									<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
										Save &amp; Continue
									</CerebroPrimaryButton>
								</CerebroForm>
							)}
						</Formik>

						<DeleteExcludedLenderDialog
							dealGuid={guid}
							lender={excludedLenderToDelete}
							close={onDrawersClose}
						/>
						<ExcludedLenderDrawer
							open={excludedLenderDrawerOpen}
							onClose={onDrawersClose}
							deal={dealQuery.data}
							lenders={excludedLendersQuery.data}
						/>
					</>
				)}
			</CerebroPageContainer>
		</>
	);
};

const ExcludedLendersSchema = Yup.object().shape({
	hasExcludedLenders: Yup.boolean().nullable(true).required('Please indicate whether you have lenders to exclude.'),
	hasReducedFeeLenders: Yup.boolean()
		.nullable(true)
		.required('Please indicate whether you have reduced fee lenders to include.'),
	lenderClarifications: Yup.string(),
	lenderNotes: Yup.string(),

	excludedLenders: Yup.array()
		.of(Yup.object())
		.when('hasExcludedLenders', {
			is: true,
			then: schema =>
				schema.min(
					1,
					'You indicated that you have lenders to exclude but did not specify any lender to exclude from the loan. Please exclude at least one lender from the loan.'
				),
			otherwise: schema => schema,
		}),
	reducedFeeLenders: Yup.array()
		.of(Yup.object())
		.when('hasReducedFeeLenders', {
			is: true,
			then: schema =>
				schema.min(
					1,
					'You indicated that you have reduced fee lenders to include but did not specify any lender for this loan. Please include at least one lender for this loan.'
				),
			otherwise: schema => schema,
		}),
});

interface ExcludedLendersFormValues {
	hasExcludedLenders: boolean | null;
	hasReducedFeeLenders: boolean | null;
	lenderClarifications: string;
	lenderNotes: string;
	excludedLenders: DealExcludedLender[];
	reducedFeeLenders: DealReducedFeeLender[];
}
type DealMutationVariables = FormikUtils.MutateOnSubmitVariables<ExcludedLendersFormValues>;
